import React, { useEffect, useState } from "react";
import {
  Collapse,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  Nav,
  NavItem,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useExternalUrls } from "../contexts/ExternalUrlsContext";
import { getPublicFlagUrl } from "../utility/publicFlagUrl";
import { getPublicLanguages } from "../utility/publicLanguageStorage";
import "./NavMenu.css";

function languagesMatchCurrent(storedLng, apiCode) {
  if (!storedLng || !apiCode) return false;
  const a = storedLng.toLowerCase();
  const b = apiCode.toLowerCase();
  if (a === b) return true;
  return a.split("-")[0] === b.split("-")[0];
}

export const NavMenu = () => {
  const [collapsed, setCollapsed] = useState(true);
  const [languages, setLanguages] = useState([]);
  const { t, i18n } = useTranslation();
  const { corridor10 } = useExternalUrls();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPublicLanguages();
        if (cancelled || !Array.isArray(data)) return;
        setLanguages(data);

        const lng = localStorage.getItem("i18nextLng");
        if (lng === "en-US") {
          const def = data.find((l) => l.isDefault);
          if (def?.i18nCode?.code) {
            localStorage.setItem("i18nextLng", def.i18nCode.code);
            localStorage.setItem("i18nextLanguageId", def.id);
            await i18n.changeLanguage(def.i18nCode.code);
          }
        } else if (lng) {
          const match = data.find((l) =>
            languagesMatchCurrent(lng, l.i18nCode?.code)
          );
          if (match?.id) {
            localStorage.setItem("i18nextLanguageId", match.id);
          }
        }
      } catch {
        /* keep defaults; dropdown may be empty */
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally once on mount: language list is static for the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLanguageSelect = (lang) => {
    if (!lang?.i18nCode?.code) return;
    localStorage.setItem("i18nextLng", lang.i18nCode.code);
    localStorage.setItem("i18nextLanguageId", lang.id);
    i18n.changeLanguage(lang.i18nCode.code);
  };

  const isLanguageSelected = (lang) =>
    languagesMatchCurrent(i18n.language, lang?.i18nCode?.code);

  return (
    <header className="site-header">
      <Navbar expand="md" className="site-navbar" container>
        <div className="site-brand-wrap">
          <NavbarBrand className="site-brand" tag={Link} to="/">
            <img src={require("../assets/corridor10_logo.png")} alt="Corridor 10" />
          </NavbarBrand>
          <NavbarToggler onClick={() => setCollapsed(!collapsed)} className="site-toggler" />
        </div>
        <Collapse isOpen={!collapsed} navbar className="site-collapse">
          <Nav navbar className="header-actions">
            <UncontrolledDropdown nav inNavbar className="register-nav-item">
              <DropdownToggle
                nav
                caret
                className="header-pill header-pill-dark register-trigger"
              >
                {t("menu:register-tab","Register")}
              </DropdownToggle>
              <DropdownMenu end className="register-menu">
                <DropdownItem tag={Link} to="/requests/create/1" className="register-menu-item">
                  {t("menu:register-as-purchaser","Register as Purchaser")}
                </DropdownItem>
                <DropdownItem tag={Link} to="/requests/create/2" className="register-menu-item">
                  {t("menu:register-as-carrier","Register as Carrier")}
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
            <NavItem className="header-login-item">
              <a href={corridor10} className="header-pill header-pill-dark" target="_blank" rel="noopener noreferrer">
                {t("landing:auth.login","Login")}
              </a>
            </NavItem>
            {/* Do not use inNavbar here: reactstrap skips Popper <Reference> on the toggle in navbar mode,
                which breaks placement (menu jumps to top-left). Regular nav dropdown still gets nav-link styling. */}
            <UncontrolledDropdown nav className="language-nav-item-wrap">
              <DropdownToggle
                nav
                caret={false}
                className="language-dropdown-toggle"
                aria-label={t("landing:language.menu", "Language")}
              >
                <span className="lang-en-icon" aria-hidden="true">
                  <img
                    src={getPublicFlagUrl(i18n.language)}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "/flags/en.svg";
                    }}
                  />
                </span>
              </DropdownToggle>
              <DropdownMenu
                end
                flip={false}
                modifiers={[
                  {
                    name: "offset",
                    options: { offset: [0, 8] },
                  },
                ]}
                className="language-dropdown-menu"
              >
                {languages.map((lang) => {
                  const selected = isLanguageSelected(lang);
                  return (
                    <DropdownItem
                      key={lang.id}
                      tag="button"
                      type="button"
                      className={
                        selected
                          ? "language-menu-item language-menu-item-active"
                          : "language-menu-item"
                      }
                      onClick={() => handleLanguageSelect(lang)}
                    >
                      <span className="language-menu-row">
                        <img
                          className="language-menu-flag"
                          src={getPublicFlagUrl(lang.i18nCode?.code)}
                          alt=""
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/flags/en.svg";
                          }}
                        />
                        <span className="language-menu-label">{lang.i18nCode?.name}</span>
                      </span>
                    </DropdownItem>
                  );
                })}
              </DropdownMenu>
            </UncontrolledDropdown>
          </Nav>
        </Collapse>
      </Navbar>
    </header>
  );
};
